import React, { useState, useCallback } from 'react';
import { BarChart3, Loader2, Download, Calendar, ArrowUpDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const ARAgingReport = () => {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ field: 'total', dir: 'desc' });
  const printRef = React.useRef(null);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/ar-aging?as_of_date=${asOfDate}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load report');
      setData(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [asOfDate]);

  const toggleSort = (field) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
  };

  const sortedRows = data ? [...data.rows].sort((a, b) => {
    const cmp = sort.field === 'customer_name'
      ? a.customer_name.localeCompare(b.customer_name)
      : a[sort.field] - b[sort.field];
    return sort.dir === 'desc' ? -cmp : cmp;
  }) : [];

  const handlePDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `AR_Aging_Report_${asOfDate}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
      }).from(printRef.current).save();
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const cols = [
    { key: 'customer_name', label: 'Customer', align: 'left', sortable: true },
    { key: 'current', label: 'Current', align: 'right', sortable: true },
    { key: 'days_30', label: '1-30 Days', align: 'right', sortable: true },
    { key: 'days_60', label: '31-60 Days', align: 'right', sortable: true },
    { key: 'days_90', label: '61-90 Days', align: 'right', sortable: true },
    { key: 'days_120_plus', label: 'Over 90', align: 'right', sortable: true },
    { key: 'total', label: 'Total Due', align: 'right', sortable: true },
  ];

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="ar-aging-print"], [data-testid="ar-aging-print"] * { visibility: visible; }
          [data-testid="ar-aging-print"] { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          [data-testid="no-print"] { display: none !important; }
        }
      `}</style>

      <div className="space-y-6" data-testid="ar-aging-page">
        {/* Controls */}
        <div className="bg-white rounded-lg border p-6" data-testid="no-print">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#8B7355]" />
            AR Aging Report
          </h2>
          <div className="flex items-end gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">As of Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)}
                  className="pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="ar-aging-date-input" />
              </div>
            </div>
            <Button onClick={generate} disabled={loading} className="bg-[#8B7355] hover:bg-[#6B5B4A] text-white" data-testid="ar-aging-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
            {data && (
              <>
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="ar-aging-print-btn">Print</Button>
                <Button variant="outline" size="sm" onClick={handlePDF} disabled={exporting} data-testid="ar-aging-pdf-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} PDF
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Report */}
        {data && (
          <div ref={printRef} data-testid="ar-aging-print" className="bg-white rounded-lg border" style={{ fontFamily: 'Arial, sans-serif' }}>
            <div className="flex justify-between items-start p-8 pb-4">
              <div>
                <h1 className="text-2xl font-bold text-[#8B7355]">Accounts Receivable Aging</h1>
                <p className="text-sm text-gray-500 mt-1">As of {data.as_of_date} | {data.customer_count} customer{data.customer_count !== 1 ? 's' : ''} with balances</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wider">Total Outstanding</p>
                <p className="text-2xl font-bold text-[#8B7355]" data-testid="ar-aging-grand-total">{fmt(data.totals.total)}</p>
              </div>
            </div>

            <div className="px-8 pb-6">
              <table className="w-full text-sm border-collapse" data-testid="ar-aging-table">
                <thead>
                  <tr className="bg-[#f5f5f0] border-y">
                    {cols.map(col => (
                      <th key={col.key}
                        className={`px-3 py-2 font-semibold text-gray-700 text-${col.align} ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                        onClick={col.sortable ? () => toggleSort(col.key) : undefined}>
                        <span className="flex items-center gap-1" style={{ justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                          {col.label}
                          {col.sortable && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50/60" data-testid={`ar-aging-row-${i}`}>
                      <td className="px-3 py-2 font-medium">{row.customer_name}</td>
                      <td className="px-3 py-2 text-right">{fmt(row.current)}</td>
                      <td className="px-3 py-2 text-right">{row.days_30 > 0 ? fmt(row.days_30) : '—'}</td>
                      <td className="px-3 py-2 text-right">{row.days_60 > 0 ? fmt(row.days_60) : '—'}</td>
                      <td className="px-3 py-2 text-right">{row.days_90 > 0 ? fmt(row.days_90) : '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold" style={{ color: row.days_120_plus > 0 ? '#dc2626' : undefined }}>{row.days_120_plus > 0 ? fmt(row.days_120_plus) : '—'}</td>
                      <td className="px-3 py-2 text-right font-bold">{fmt(row.total)}</td>
                    </tr>
                  ))}
                  {sortedRows.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No outstanding balances</td></tr>
                  )}
                  {sortedRows.length > 0 && (
                    <tr className="bg-[#f5f5f0] border-t-2 border-gray-300 font-bold">
                      <td className="px-3 py-2">TOTALS</td>
                      <td className="px-3 py-2 text-right">{fmt(data.totals.current)}</td>
                      <td className="px-3 py-2 text-right">{fmt(data.totals.days_30)}</td>
                      <td className="px-3 py-2 text-right">{fmt(data.totals.days_60)}</td>
                      <td className="px-3 py-2 text-right">{fmt(data.totals.days_90)}</td>
                      <td className="px-3 py-2 text-right">{fmt(data.totals.days_120_plus)}</td>
                      <td className="px-3 py-2 text-right text-[#8B7355]">{fmt(data.totals.total)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-8 pb-6">
              <p className="text-xs text-gray-400">Report generated on {new Date().toLocaleDateString()} | Amounts in USD</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ARAgingReport;
